/**
 * Type Detector Registry
 * 
 * Manages type detectors and runs them in priority order.
 */

import type { ISemanticType } from '@/interfaces/ISemanticType';
import type { DetectionOptions,ITypeDetector } from '@/interfaces/ITypeDetector';

import { SEMANTIC_TYPES } from './semanticTypes';

/**
 * Registry for type detectors.
 */
class DetectorRegistryImpl {
  private detectors: ITypeDetector[] = [];

  /**
   * Register a new detector.
   */
  register(detector: ITypeDetector): void {
    this.detectors.push(detector);
    // Keep sorted by priority (highest first)
    this.detectors.sort((a, b) => b.priority - a.priority);
  }

  /**
   * Get all registered detectors, sorted by priority.
   */
  getAll(): ITypeDetector[] {
    return [...this.detectors];
  }

  /**
   * Filter detectors based on options.
   */
  private filterDetectors(options?: DetectionOptions): ITypeDetector[] {
    let filtered = this.detectors;

    if (options?.minPriority !== undefined) {
      filtered = filtered.filter(d => d.priority >= options.minPriority!);
    }

    if (options?.onlyDetectors) {
      filtered = filtered.filter(d => options.onlyDetectors!.includes(d.id));
    }

    if (options?.skipDetectors) {
      filtered = filtered.filter(d => !options.skipDetectors!.includes(d.id));
    }

    return filtered;
  }

  /**
   * Detect the type of input by running detectors in priority order.
   * Returns the first match, or TEXT type as fallback.
   * 
   * @param input - The string to analyze
   * @param options - Optional filtering for which detectors to run
   */
  detect(input: string, options?: DetectionOptions): ISemanticType {
    if (!input || input.trim().length === 0) {
      return SEMANTIC_TYPES.TEXT;
    }

    const detectorsToRun = this.filterDetectors(options);

    for (const detector of detectorsToRun) {
      const result = detector.detect(input);
      if (result) {
        return result;
      }
    }

    // Default to plain text
    return SEMANTIC_TYPES.TEXT;
  }

  /**
   * Detect all possible types with confidence scores.
   * 
   * @param input - The string to analyze
   * @param options - Optional filtering for which detectors to run
   */
  detectAll(input: string, options?: DetectionOptions): Array<{ type: ISemanticType; confidence: number }> {
    if (!input || input.trim().length === 0) {
      return [{ type: SEMANTIC_TYPES.TEXT, confidence: 100 }];
    }

    const detectorsToRun = this.filterDetectors(options);
    const results: Array<{ type: ISemanticType; confidence: number }> = [];

    for (const detector of detectorsToRun) {
      const result = detector.detect(input);
      if (result) {
        results.push({ type: result, confidence: detector.priority });
      }
    }

    if (results.length === 0) {
      results.push({ type: SEMANTIC_TYPES.TEXT, confidence: 50 });
    }

    return results;
  }

  /**
   * Clear all detectors (mainly for testing).
   */
  clear(): void {
    this.detectors = [];
  }
}

export const DetectorRegistry = new DetectorRegistryImpl();

